import React, { useEffect, useCallback } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Store } from "./store";
import clone from "clone";
import Cookies from "js-cookie";
import gql from "graphql-tag";
import { USER, ADDRESS } from "./types";
import { ITEM } from "./hooks/useInventory";
import useNotification from "./hooks/useNotification";

export default props => {
  const { checkout, updateAndSetCheckout, setHomeOpen } = Store.useState(s => s);
  const { addNotification } = useNotification();
  const storedId = Cookies.get("checkout-id");

  const [getCheckout, { error, data: checkoutData }] = useLazyQuery(GET_CHECKOUT);
  const [createCheckout, { called: createCheckoutCalled }] = useMutation(POST_CHECKOUT);
  const [updateCheckout] = useMutation(POST_CHECKOUT_UPDATE);
  const [updateCheckoutItems] = useMutation(POST_CHECKOUT_UPDATE_ITEMS);
  const [validateVoucher] = useMutation(POST_VALIDATE_VOUCHER);
  const [addVoucher] = useMutation(ADD_VOUCHER);
  const [removeVoucher] = useMutation(REMOVE_VOUCHER);

  const setCheckout = checkout =>
    Store.update(s => {
      s.checkout = clone(checkout);
    });

  const createNewCheckout = useCallback(async () => {
    try {
      const {
        data: { checkoutCreate: checkout }
      } = await createCheckout();
      Cookies.set("checkout-id", checkout.id, { expires: 365 });
      setCheckout(checkout);
    } catch (e) {
      addNotification({ ok: 0, message: e.toString() });
    }
  }, [createCheckout, addNotification]);

  useEffect(() => {
    if (storedId) getCheckout({ variables: { id: storedId } });
  }, [storedId, getCheckout]);

  useEffect(() => {
    if ((error || !storedId) && !createCheckoutCalled) createNewCheckout();
  }, [error, createNewCheckout, createCheckoutCalled, storedId]);

  useEffect(() => {
    const checkout = checkoutData && checkoutData.checkout;
    if (checkout) setCheckout(checkout);
  }, [checkoutData]);

  useEffect(() => {
    Store.update(s => {
      s.updateCheckout = async checkoutData => {
        try {
          const { data } = await updateCheckout({ variables: { id: checkout.id, ...checkoutData } });
          setCheckout(data.checkoutUpdate);
        } catch (e) {
          createNewCheckout();
          throw e;
        }
      };
      s.addVoucher = addVoucher;
      s.removeVoucher = removeVoucher;
      s.createNewCheckout = createNewCheckout;
    });
  }, [updateCheckout, removeVoucher, addVoucher, checkout, createNewCheckout]);

  useEffect(() => {
    Store.update(s => {
      s.updateBasket = async listingId => {
        try {
          const items = checkout.items.map(i => ({
            listingId: parseFloat(i.listing.id),
            quantity: i.quantity
          }));
          const index = items.findIndex(i => i.listingId === listingId);
          if (index > -1) items.splice(index, 1);
          else items.push({ listingId, quantity: 1 });
          const { data } = await updateCheckoutItems({ variables: { id: checkout.id, items } });
          setCheckout(data.checkoutUpdateItems);
          addNotification({ ok: 1, message: <button onClick={e => setHomeOpen(true)}>Basket updated</button> });
        } catch (e) {
          if (e.toString() === "Error: Checkout not found") createNewCheckout();
          addNotification({ ok: 0, message: e.message });
        }
      };

      s.checkoutUpdateQuantity = async (listingId, quantity) => {
        try {
          const items = checkout.items.map(i => ({
            listingId: parseFloat(i.listing.id),
            quantity: i.quantity
          }));
          const index = items.findIndex(i => i.listingId === listingId);
          if (index === -1) return addNotification({ ok: 0, message: "Basket entry not found" });
          if (quantity <= 0) items.splice(index, 1);
          else items[index].quantity = quantity;
          const {
            data: { checkoutUpdateItems: updatedCheckout }
          } = await updateCheckoutItems({ variables: { id: checkout.id, items } });
          setCheckout(updatedCheckout);
          return updatedCheckout;
        } catch (e) {
          if (e.toString() === "Error: Checkout not found") createNewCheckout();
          addNotification({ ok: 0, message: e.message });
        }
      };
    });
  }, [addNotification, checkout, updateCheckoutItems, createNewCheckout]);

  useEffect(() => {
    Store.update(s => {
      s.addVoucher = async id => {
        try {
          await validateVoucher({ variables: { id: checkout.id, voucherId: id } });
          const { data } = await addVoucher({ variables: { id: checkout.id, voucherId: id } });
          return data;
        } catch (e) {
          return { ok: 0, message: e.toString() };
        }
      };

      s.removeVoucher = async () => {
        await removeVoucher({ variables: { id: checkout.id } });
      };
    });
  }, [checkout, validateVoucher, addVoucher, removeVoucher, updateAndSetCheckout]);

  if (checkout) return props.children;
  else return null;
};

const CHECKOUT_PRICE = `
  base
  sale
  compare
`;

export const CHECKOUT = `
  _id
  id
  buyer {
    _id
    email
    firstName
    id
    lastName
    name
    telephone
    organisation
    taxNumber
  }
  shipping {
    status
    date
    price {
      base
    }
    method
    methods  {
      name
      total
    }
    freeShippingThreshold
    address {
      ${ADDRESS}
    }
    tracking
    shopCollection
    weight
  }
  billing {
    status
    address {
      ${ADDRESS}
    }
    voucher {
      id
      redeemType
      expires
      value
    }
  }
  orderId
  items {
    _id
    id
    listing {
      _id
      id
      prices {
        ${CHECKOUT_PRICE}
      }
      onePerCustomer
      preOrder
      stock {
        quantity
      }
      available
      options {
        name
        value
      }
    }
    item {
      id
      description
      thumbnail
      weight
      path
    }
    quantity
    price {
      base
      beforeTaxes
      subtotal
      subtotalBeforeTaxes
    }
  }
  totals {
    grand
    leftToPay
    discount
    itemQuantity
    subtotal
    shipping
    taxes {
      name
      amount
    }
  }
  urls {
    storefront
    checkout
  }
`;

const SESSION = `
  session {
    user {
      ${USER}
    }
    jwt
    expires
  }
`;

const ORDER = `
  _id
  id
  origin
  status
  created
  paymentUrl
  buyer {
    name
    email
    firstName
    lastName
  }
  messages {
    content
    subject
    created
    fromBuyer
    attachments {
      _id
      uri
      title
    }
    sender {
      name
      alias
    }
  }
  billing {
    status
    invoice {
      date
      pdf
      id
    }
    paymentMethods {
      origin
      amount
    }
  }
  shipping {
    tracking
    shopCollection
    address {
      alpha2
    }
  }
  items {
    listing {
      options {
        name
        value
      }
    }
    item {
      id
      description
      thumbnail
      path
    }
    quantity
    price {
      subtotal
    }
    unavailable
  }
  totals {
    subtotal
    shipping
    grand
    discount
    itemQuantity
  }
`;

export const GET_CHECKOUT = gql(`
  query($id: String!) {
    checkout(id: $id) {
      ${CHECKOUT}
    }
}`);

export const POST_LOGIN = gql(`
  mutation($password: String!, $email: String!) {
    login(password: $password, email: $email) {
      ${SESSION}
      sessionsCount
    }
}`);

export const POST_RELEASE_SNIPPET_ARCHIVE = gql(`
  mutation($id: Float!) {
    itemSnippetsArchive(id: $id)
}`);

export const POST_CHECKOUT = gql(`
  mutation {
    checkoutCreate {
      ${CHECKOUT}
    }
}`);

export const POST_CHECKOUT_UPDATE = gql(`
  mutation($id: String!, $buyer: CheckoutBuyerInput, $shipping: CheckoutShippingInput, $billing: CheckoutBillingInput) {
    checkoutUpdate(id: $id, buyer: $buyer, shipping: $shipping, billing: $billing) {
      ${CHECKOUT}
    }
}`);

export const POST_CHECKOUT_VERIFY = gql(`
  mutation($id: String!) {
    checkoutVerifyAndUpdate(id: $id) {
      checkout {
        ${CHECKOUT}
      }
      message
    }
  }
`);

export const POST_LIST_OPTIN = gql(`
  mutation($listRef: ID!, $email: String!) {
    listOptin(listRef: $listRef, email: $email)
  }
`);

export const POST_LIST_OPTOUT = gql(`
  mutation($listRef: ID!, $email: String!, $reason: String!) {
    listOptout(listRef: $listRef, email: $email, reason: $reason)
  }
`);

export const POST_ORDER_MESSAGE = gql(`
  mutation($id: String!, $message: String!) {
    order: orderAddMessage(id: $id, message: $message) {
      ${ORDER}
    }
}`);

export const POST_CHECKOUT_UPDATE_ITEMS = gql(`
  mutation($id: String!, $items: [CheckoutItemInput]) {
    checkoutUpdateItems(id: $id, items: $items) {
      ${CHECKOUT}
    }
  }
`);

export const POST_VALIDATE_VOUCHER = gql(`
  mutation($id: String!, $voucherId: String!) {
    validateVoucher(id: $id, voucherId: $voucherId) {
      id
    }
  }
`);

export const ADD_VOUCHER = gql(`
  mutation($id: String!, $voucherId: String!) {
    addVoucher(id: $id, voucherId: $voucherId) {
      ${CHECKOUT}
    }
  }
`);

export const REMOVE_VOUCHER = gql(`
  mutation($id: String!) {
    removeVoucher(id: $id) {
      ${CHECKOUT}
    }
  }
`);

export const POST_REGISTER = gql(`
  mutation($password: String!, $email: String!, $firstName: String!, $lastName: String!, $withVerificationCode: Boolean) {
    register(password: $password, email: $email, firstName: $firstName, lastName: $lastName, withVerificationCode: $withVerificationCode) {
      message
    }
}`);

export const POST_USER_UPDATE = gql(`
  mutation($email: String, $password: String, $firstName: String, $lastName: String, $telephone: String, $organisation: String, $taxNumber: String, $addresses: [AddressInput], $preferences: JSON) {
    user: userUpdate(email: $email, password: $password, firstName: $firstName, lastName: $lastName, telephone: $telephone, organisation: $organisation, taxNumber: $taxNumber, addresses: $addresses, preferences: $preferences) {
      ${USER}
    }
}`);

export const POST_LOGOUT = gql(`
  mutation {
    logout {
      ok
    }
}`);

export const POST_WANT = gql(`
  mutation($id: Float!) {
    want(id: $id) {
      ${ITEM}
    }
  }
`);

export const POST_REGISTER_EVENT = gql(`
  mutation($type: String!, $data: JSON!) {
    registerEvent(type: $type, data: $data) {
      id
    }
  }
`);

export const POST_PLAY_COUNT = gql(`
  mutation($id: Float!, $position: String!) {
    playCount(id: $id, position: $position)
  }
`);

export const POST_STRIPE_INTENT = gql(`
  mutation($documentId: String!, $paymentMethodId: String, $provider: String) {
    stripeCreateIntent(documentId: $documentId, paymentMethodId: $paymentMethodId, provider: $provider) {
      intent
      results {
        success
        requires_action
        payment_intent_client_secret
        error
      }
    }
  }
`);

export const POST_STRIPE_CONFIRM_INTENT = gql(`
  mutation($documentId: String!, $intentId: String!) {
    stripeConfirmIntent(intentId: $intentId, documentId: $documentId) {
      intent
      results {
        success
        error
      }
    }
  }
`);

export const POST_CHECKOUT_SUBMIT = gql(`
  mutation($checkoutId: String!, $paymentData: JSON) {
    checkoutSubmit(checkoutId: $checkoutId, paymentData: $paymentData) {
      order {
        id
      }
    }
  }
`);

export const GET_ORDERS = gql(`
    query {
      orders {
        ${ORDER}
      }
  }`);

export const GET_ORDER = gql(`
  query($id: String!) {
    order(id: $id) {
      ${ORDER}
    }
}`);
